// The signed agreements of one owner — a retainer or a job — as rows.
//
// No owner column: this list only ever appears INSIDE the record it belongs to — the fold
// on the client card, the retainer's own screen, or the job's. A column repeating what you
// are already looking at is noise, and the earlier flat version of this list (a client-card
// tab of every agreement at once) is gone precisely because a row divorced from its owner
// had to spend a column saying where it came from.
//
// Files are never streamed through our server: FileActions asks for a short-lived
// signed URL and hands it to the browser. `api.url` is what points those actions at the
// right agreements endpoint — the retainer's or the job's — instead of the publications
// one. It arrives as a prop rather than being imported so this list can render either
// owner's contracts without knowing which it is looking at.
import he from '../i18n/he';
import DataTable from './DataTable';
import DateText from './DateText';
import Button from './Button';
import IconButton from './IconButton';
import { FileActions } from './FileList';
import { TrashIcon } from './icons';

export default function AgreementList({
  agreements, archived, api, onEdit, onRemove, onRestore, empty,
}) {
  const columns = [
    // Title leads, with the real filename beneath it: a partner names an agreement
    // something they will recognise, but "which scan is this" is answered by the file.
    { key: 'title', label: he.agreements.name, primary: true, render: (r) => (
      <>
        <div>{r.title}</div>
        <span className="muted small-note" title={r.original_name}>
          {r.original_name}
        </span>
      </>
    ) },
    // The date on the CONTRACT, not the upload date — the whole reason the column
    // exists is that the two are routinely years apart.
    { key: 'signed_date', label: he.agreements.signedDate, render: (r) => <DateText value={r.signed_date} /> },
    { key: 'uploaded_by_partner_name', label: he.agreements.uploadedBy, render: (r) => (
      archived
        // In the archive, who put it away is the more useful fact than who uploaded it.
        ? <span title={he.agreements.archivedBy}>{r.archived_by_partner_name || '—'}</span>
        : <span>{r.uploaded_by_partner_name || '—'}</span>
    ) },
    { key: 'actions', label: he.common.actions, action: true, render: (r) => (
      <div className="row">
        {/* The same view/download actions every uploaded file in this app has,
            reused rather than rebuilt — only the signed-URL source differs. */}
        <FileActions file={r} getUrl={api.url}>
          {!archived && onRemove && (
            <IconButton
              icon={<TrashIcon />}
              label={he.agreements.remove}
              danger
              onClick={() => onRemove(r)}
            />
          )}
        </FileActions>
        {archived
          ? <Button small variant="subtle" onClick={() => onRestore(r)}>{he.agreements.restore}</Button>
          : onEdit && <Button small variant="subtle" onClick={() => onEdit(r)}>{he.common.edit}</Button>}
      </div>
    ) },
  ];

  return <DataTable columns={columns} rows={agreements} empty={empty} />;
}
